import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Target, TrendingUp, Calendar, Save, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import LoadingSpinner from "@/components/LoadingSpinner";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import StoreGoalCard from "@/components/metas/StoreGoalCard";
import DailySalesDialog from "@/components/metas/DailySalesDialog";
import AddGoalDialog from "@/components/metas/AddGoalDialog";
import WeeklyGoalCard from "@/components/metas/WeeklyGoalCard";
import AddWeeklyGoalDialog from "@/components/metas/AddWeeklyGoalDialog";
import { useLojas } from "@/hooks/useLojas";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Metas = () => {
  const LOJAS = useLojas();
  const [loading, setLoading] = useState(true);
  const [metasMensais, setMetasMensais] = useState([]);
  const [metasSemanais, setMetasSemanais] = useState([]);
  const [statsAtual, setStatsAtual] = useState(null);
  const [aba, setAba] = useState("mensal");
  const [dailyConfig, setDailyConfig] = useState({ loja: null });
  const [dailyOpen, setDailyOpen] = useState(false);
  const [vendasSemanais, setVendasSemanais] = useState({});
  
  // Form state para nova meta mensal
  const [novaMetaMensal, setNovaMetaMensal] = useState({
    loja: "",
    mes: new Date().getMonth() + 1,
    ano: new Date().getFullYear(),
    valor_meta: ""
  });
  
  // Form state para nova meta semanal
  const [novaMetaSemanal, setNovaMetaSemanal] = useState({
    loja: "",
    data_inicio: "",
    data_fim: "",
    valor_meta: ""
  });

  useEffect(() => {
    carregarDados();
  }, []);

  useEffect(() => {
    if (!metasSemanais || metasSemanais.length === 0) {
      setVendasSemanais({});
      return;
    }
    let ativo = true;
    (async () => {
      const resultados = await Promise.all(
        metasSemanais.map(async (m) => {
          try {
            const res = await axios.get(`${API}/dashboard/conferencia-mensal`, {
              params: { data_inicio: m.data_inicio, data_fim: m.data_fim, loja: m.loja },
            });
            return [m.id, res.data.total_mes || 0];
          } catch {
            return [m.id, 0];
          }
        })
      );
      if (ativo) setVendasSemanais(Object.fromEntries(resultados));
    })();
    return () => { ativo = false; };
  }, [metasSemanais]);

  const carregarDados = async () => {
    try {
      setLoading(true);
      const hoje = new Date();
      const inicioMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
      const fimMes = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);

      const [statsRes, metasMensaisRes, metasSemanaisRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, {
          params: {
            start_date: inicioMes.toISOString(),
            end_date: fimMes.toISOString(),
          },
        }),
        axios.get(`${API}/metas`, {
          params: { mes: hoje.getMonth() + 1, ano: hoje.getFullYear() },
        }),
        axios.get(`${API}/metas/semanais`).catch(() => ({ data: [] })),
      ]);

      setStatsAtual(statsRes.data);
      setMetasMensais(metasMensaisRes.data || []);
      setMetasSemanais(metasSemanaisRes.data || []);
    } catch (error) {
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  const salvarMetaMensal = async () => {
    if (!novaMetaMensal.loja || !novaMetaMensal.valor_meta) {
      toast.error("Preencha loja e valor da meta");
      return false;
    }
    try {
      await axios.post(`${API}/metas`, [{
        loja: novaMetaMensal.loja,
        mes: parseInt(novaMetaMensal.mes),
        ano: parseInt(novaMetaMensal.ano),
        valor_meta: parseFloat(novaMetaMensal.valor_meta)
      }]);
      toast.success("Meta mensal salva!");
      setNovaMetaMensal({ loja: "", mes: new Date().getMonth() + 1, ano: new Date().getFullYear(), valor_meta: "" });
      carregarDados();
      return true;
    } catch {
      toast.error("Erro ao salvar meta");
      return false;
    }
  };

  const abrirVendasDiarias = (loja) => {
    setDailyConfig({ loja });
    setDailyOpen(true);
  };

  const abrirVendasSemana = (meta) => {
    const f = (d) => { const [, m, dia] = (d || "").split("-"); return `${dia}/${m}`; };
    setDailyConfig({
      loja: meta.loja,
      dataInicio: meta.data_inicio,
      dataFim: meta.data_fim,
      subtitulo: `Semana ${f(meta.data_inicio)} - ${f(meta.data_fim)}`,
    });
    setDailyOpen(true);
  };

  const salvarMetaSemanal = async () => {
    if (!novaMetaSemanal.loja || !novaMetaSemanal.data_inicio || !novaMetaSemanal.data_fim || !novaMetaSemanal.valor_meta) {
      toast.error("Preencha todos os campos");
      return false;
    }
    try {
      await axios.post(`${API}/metas/semanais`, {
        loja: novaMetaSemanal.loja,
        data_inicio: novaMetaSemanal.data_inicio,
        data_fim: novaMetaSemanal.data_fim,
        valor_meta: parseFloat(novaMetaSemanal.valor_meta)
      });
      toast.success("Meta semanal salva!");
      setNovaMetaSemanal({ loja: "", data_inicio: "", data_fim: "", valor_meta: "" });
      carregarDados();
      return true;
    } catch {
      toast.error("Erro ao salvar meta semanal");
      return false;
    }
  };

  const excluirMetaSemanal = async (id) => {
    if (!window.confirm("Excluir esta meta semanal?")) return;
    try {
      await axios.delete(`${API}/metas/semanais/${id}`);
      toast.success("Meta excluída");
      carregarDados();
    } catch {
      toast.error("Erro ao excluir");
    }
  };

  const atualizarMetaMensal = async (loja, valor) => {
    const hoje = new Date();
    try {
      await axios.post(`${API}/metas`, [{
        loja,
        mes: hoje.getMonth() + 1,
        ano: hoje.getFullYear(),
        valor_meta: parseFloat(valor)
      }]);
      toast.success(`Meta de ${loja} atualizada!`);
      carregarDados();
    } catch {
      toast.error("Erro ao atualizar meta");
    }
  };

  const fmt = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

  const getVendasLoja = (loja) => {
    if (!statsAtual?.vendas_por_loja) return 0;
    const lojaData = statsAtual.vendas_por_loja.find((l) => l.loja === loja);
    return lojaData?.valor || 0;
  };

  const getMetaLoja = (loja) => {
    const meta = metasMensais.find((m) => m.loja === loja);
    return meta?.valor_meta || 0;
  };

  const calcularProgresso = (vendas, meta) => {
    if (!meta || meta === 0) return 0;
    return Math.min(100, Math.round((vendas / meta) * 100));
  };

  if (loading) {
    return (
      <div className="py-12">
        <LoadingSpinner message="Carregando metas..." />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="metas-container">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <Target className="w-8 h-8 text-slate-700" />
            Metas de Vendas
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Configure e acompanhe metas mensais e semanais por loja
          </p>
        </div>
      </div>

      <Tabs value={aba} onValueChange={setAba} className="space-y-4">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="mensal" className="data-[state=active]:bg-white">
            <Calendar className="w-4 h-4 mr-2" />
            Metas Mensais
          </TabsTrigger>
          <TabsTrigger value="semanal" className="data-[state=active]:bg-white">
            <TrendingUp className="w-4 h-4 mr-2" />
            Metas Semanais
          </TabsTrigger>
        </TabsList>

        {/* Metas Mensais */}
        <TabsContent value="mensal" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {LOJAS.map((loja) => {
              const vendas = getVendasLoja(loja);
              const meta = getMetaLoja(loja);
              const progresso = calcularProgresso(vendas, meta);
              const atingida = progresso >= 100;
              return (
                <StoreGoalCard
                  key={loja}
                  loja={loja}
                  vendas={vendas}
                  meta={meta}
                  progresso={progresso}
                  atingida={atingida}
                  fmt={fmt}
                  onEditMeta={atualizarMetaMensal}
                  onOpenDaily={abrirVendasDiarias}
                />
              );
            })}

            <AddGoalDialog
              lojas={LOJAS}
              novaMeta={novaMetaMensal}
              setNovaMeta={setNovaMetaMensal}
              onSalvar={salvarMetaMensal}
            />
          </div>
        </TabsContent>

        {/* Metas Semanais */}
        <TabsContent value="semanal" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {metasSemanais.map((meta) => {
              const vendas = vendasSemanais[meta.id] || 0;
              const progresso = calcularProgresso(vendas, meta.valor_meta);
              const atingida = progresso >= 100;
              return (
                <WeeklyGoalCard
                  key={meta.id}
                  meta={meta}
                  vendas={vendas}
                  progresso={progresso}
                  atingida={atingida}
                  fmt={fmt}
                  onOpenDaily={abrirVendasSemana}
                  onDelete={excluirMetaSemanal}
                />
              );
            })}

            <AddWeeklyGoalDialog
              lojas={LOJAS}
              novaMeta={novaMetaSemanal}
              setNovaMeta={setNovaMetaSemanal}
              onSalvar={salvarMetaSemanal}
            />
          </div>
        </TabsContent>
      </Tabs>

      <DailySalesDialog
        loja={dailyConfig.loja}
        dataInicio={dailyConfig.dataInicio}
        dataFim={dailyConfig.dataFim}
        subtitulo={dailyConfig.subtitulo}
        open={dailyOpen}
        onOpenChange={setDailyOpen}
        fmt={fmt}
      />
    </div>
  );
};

export default Metas;
